import React from "react";
import IManagePage, { TManagePage } from "./IManagePage";
import { unauthorizedPushNotification } from "@features/utils/reactFunctions";
import { withRouter, RouteComponentProps } from "react-router";
import { connect } from "react-redux";
import { TReducers } from "@features/reducers";
import { fundsAppConsumer } from "@features/context";
import ActionsList from "@funds/components/ActionsList/ActionsList";
import WhitePaper from "@appComponents/WhitePaper/WhitePaper";
import ManageHeader from "@funds/components/ManageHeader/ManageHeader";
import { currencyFromUrl } from "@funds/features/helpers/functions";
import { EActionTypes } from "@funds/enums/EActionTypes";
import DividedContent from "@funds/components/DividedContent/DividedContent";
import {
  userCanDeposit,
  userCanWithdraw,
  userCanBuy,
  userCanSell,
} from "@funds/features/helpers/validation";
import {
  IWalletDetails,
  buyAbilities,
  depositAbilities,
  withdrawAbilities,
  sellAbilities,
} from "@market/interfaces/IWallet";
import { RestrictedView } from "@funds/components/RestrictedView/RestrictedView";
import WithdrawForm from "@funds/components/WithdrawForm/WithdrawForm";
import { ETransactionType } from "@funds/enums/ETransactionType";
import InOutHistory from "@funds/components/InOutHistory/InOutHistory";
import { TUpdateWalletData } from "@funds/interfaces/IFundsGeneral";
import { updateWalletBalanceAfterTransaction } from "@funds/features/action.funds";
import DepositForm from "@funds/components/DepositForm/DepositForm";
import PurchaseForm from "@funds/components/PurchaseForm/PurchaseForm";
import ExchangeForm from "@funds/components/ExchangeForm/ExchangeForm";
import { ENHANCED_MARKET, MAIN_CURRENCY } from "@bundles/consts";
import { TSupportedTradingCurrencies } from "@funds/components/ExchangeForm/IExchangeForm";
import { setFundsData } from "@funds/features/action.funds";

import * as styles from "./ManagePageStyles.module.scss";

type TAbilities = {
  userCanDeposit: string | true;
  userCanWithdraw: string | true;
  userCanBuy: string | true;
  userCanSell: string | true;
};
export class ManagePage extends React.Component<IManagePage> {
  state: TManagePage = {
    selectedWallet: this.props.supportedUserWallets.find(
      (wallet) =>
        wallet.currency === currencyFromUrl(this.props.location.search)
    ),
  };

  constructor(props: IManagePage) {
    super(props);
    const currency = currencyFromUrl(this.props.location.search);
    props.setFundsData(props.investorId).then(() => {
      if (!currency && this.checkWalletExists(MAIN_CURRENCY)) {
        window.location.replace(`?currency=${MAIN_CURRENCY}`);
      } else if (!currency || !this.checkWalletExists(currency)) {
        unauthorizedPushNotification(this.unauthorizedRedirect);
        return;
      }
    });
  }

  renderApp = (wallet: IWalletDetails, abilities: TAbilities) => {
    const currency = currencyFromUrl(this.props.location.search);

    switch (this.props.match.params.action) {
      case EActionTypes.buy:
        return (
          <DividedContent
            leftComponent={
              abilities.userCanBuy === true ? (
                process.env.ENABLE_BINANCE_TRADING &&
                currency.toLowerCase() !== "bft" ? (
                  <ExchangeForm
                    currency={currency as TSupportedTradingCurrencies}
                    userWallets={this.props.supportedUserWallets}
                    type="buy"
                  />
                ) : (
                  <PurchaseForm currency={currency} purchaseWallet={wallet} />
                )
              ) : (
                <RestrictedView
                  action={EActionTypes.buy}
                  abilityKey={abilities.userCanBuy as buyAbilities}
                  wallet={wallet}
                  isUsInvestor={this.props.isUsInvestor}
                />
              )
            }
            rightComponent={
              <InOutHistory
                transactionType={
                  process.env.ENABLE_BINANCE_TRADING ||
                  currency.toLowerCase() !== "bft"
                    ? ETransactionType.tradeIn
                    : ETransactionType.purchase
                }
                currency={currency}
              />
            }
          />
        );

      case EActionTypes.sell:
        return (
          <DividedContent
            leftComponent={
              process.env.ENABLE_BINANCE_TRADING &&
              abilities.userCanSell === true ? (
                <ExchangeForm
                  currency={currency as TSupportedTradingCurrencies}
                  userWallets={this.props.supportedUserWallets}
                  type="sell"
                />
              ) : (
                <RestrictedView
                  action={EActionTypes.sell}
                  abilityKey={abilities.userCanSell as sellAbilities}
                  wallet={wallet}
                  isUsInvestor={this.props.isUsInvestor}
                />
              )
            }
            rightComponent={
              <InOutHistory
                transactionType={ETransactionType.tradeOut}
                currency={currency}
              />
            }
          />
        );

      case EActionTypes.deposit:
        return (
          <DividedContent
            leftComponent={
              abilities.userCanDeposit === true ? (
                <DepositForm history={this.props.history} currency={currency} />
              ) : (
                <RestrictedView
                  action={EActionTypes.deposit}
                  abilityKey={abilities.userCanDeposit as depositAbilities}
                  isUsInvestor={this.props.isUsInvestor}
                />
              )
            }
            rightComponent={
              <InOutHistory
                currency={currency}
                transactionType={ETransactionType.deposit}
              />
            }
          />
        );

      case EActionTypes.withdraw:
        return (
          <DividedContent
            leftComponent={
              abilities.userCanWithdraw === true ? (
                <WithdrawForm
                  currency={currency}
                  updateWalletAfterTransaction={
                    this.props.updateWalletAfterTransaction
                  }
                />
              ) : (
                <RestrictedView
                  action={EActionTypes.withdraw}
                  abilityKey={abilities.userCanWithdraw as withdrawAbilities}
                  verificationUrl={
                    this.props.isCompany
                      ? `/companies/${this.props.companySlug}/verification`
                      : undefined
                  }
                  securityUrl={`/profile/${this.props.investorSlug}/account?tab=security`}
                />
              )
            }
            rightComponent={
              <InOutHistory
                updateWalletOnCancel={this.props.updateWalletAfterTransaction}
                transactionType={ETransactionType.withdraw}
                currency={currency}
              />
            }
          />
        );

      case undefined:
      default:
        return (
          <WhitePaper className={styles.adjustedPaper}>
            <ActionsList
              currency={currency}
              availableActions={this.availableActions(abilities)}
            />
          </WhitePaper>
        );
    }
  };

  componentDidUpdate(prevProps: IManagePage) {
    if (this.props.location.search !== prevProps.location.search) {
      if (
        !this.checkWalletExists(currencyFromUrl(this.props.location.search))
      ) {
        unauthorizedPushNotification(this.unauthorizedRedirect);
        return;
      }
    }

    if (prevProps.supportedUserWallets !== this.props.supportedUserWallets) {
      const selectedWallet = this.props.supportedUserWallets.find(
        (wallet) =>
          wallet.currency === currencyFromUrl(this.props.location.search)
      );

      this.setState({ selectedWallet });
    }
  }

  unauthorizedRedirect = () =>
    this.props.history.replace(this.props.context.appLink);

  checkWalletExists = (currency: string) => {
    return this.props.supportedUserWallets.find(
      (el) => el.currency === currency
    );
  };

  updateCurrency = (currency: string) =>
    this.props.history.push(`?currency=${currency}`);

  actionAvailability = (ability: string | true) =>
    "restricted_by_currency" !== ability;

  availableActions = (abilities: TAbilities) => {
    const actionsArray: EActionTypes[] = [];
    const currency = currencyFromUrl(this.props.location.search);

    if (!this.state.selectedWallet) return actionsArray;
    if (this.actionAvailability(abilities.userCanWithdraw))
      actionsArray.push(EActionTypes.withdraw);

    if (!this.props.isCompany) {
      if (this.actionAvailability(abilities.userCanBuy))
        actionsArray.push(EActionTypes.buy);
      if (this.actionAvailability(abilities.userCanDeposit))
        actionsArray.push(EActionTypes.deposit);
    }

    if (currency === "usd") {
      actionsArray.push(EActionTypes.withdraw);
      actionsArray.push(EActionTypes.deposit);
    }

    if (process.env.ENABLE_BINANCE_TRADING) {
      if (this.actionAvailability(abilities.userCanSell))
        actionsArray.push(EActionTypes.sell);
    }

    return [...actionsArray];
  };

  render() {
    const { selectedWallet } = this.state;

    if (!selectedWallet) return null;

    const abilities: TAbilities = {
      userCanDeposit: userCanDeposit(selectedWallet),
      userCanWithdraw: userCanWithdraw(selectedWallet),
      userCanBuy: userCanBuy(selectedWallet),
      userCanSell: userCanSell(selectedWallet),
    };

    return (
      <>
        <ManageHeader
          currency={currencyFromUrl(this.props.location.search)}
          wallet={selectedWallet}
          availableActions={this.availableActions(abilities)}
          tier={this.props.tier}
        />
        <div className={styles.managePage}>
          {this.renderApp(selectedWallet, abilities)}
        </div>
      </>
    );
  }
}

const mapStateToProps = (store: TReducers) => ({
  supportedUserWallets: store.fundsReducer.supportedUserWallets || [],
  isCompany: !!store.companyReducer.companyDetails.investor_id,
  companySlug: store.companyReducer.companyDetails.slug,
  investorSlug: store.userInfo.nickname,
  isUsInvestor: ENHANCED_MARKET.includes(store.userInfo.country),
  tier: store.userInfo.tier,
});

const mapDispatchToProps = (dispatch: Function) => ({
  setFundsData: (investorId: number) => dispatch(setFundsData(investorId)),
  updateWalletAfterTransaction: (data: TUpdateWalletData) => {
    dispatch(updateWalletBalanceAfterTransaction(data));
  },
});

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(fundsAppConsumer(ManagePage)) as React.ComponentType<
    RouteComponentProps & { investorId: number }
  >
);
